import type { PartyProfile } from '../../../stores/authStore';
import { maskIdCard } from '../mask';

interface Props {
  profile: PartyProfile;
  onEdit: () => void;
}

const labelCls = 'text-xs text-gray-400';
const valueCls = 'text-sm font-medium text-gray-800';

export default function ProfileDisplay({ profile, onEdit }: Props) {
  const infoFields: { label: string; value: string | null }[] = [
    { label: '姓名', value: profile.fullName },
    { label: '用户名', value: profile.username },
    { label: '身份证号', value: maskIdCard(profile.idCard) },
    { label: '邮箱', value: profile.email },
    { label: '手机号', value: profile.phone },
    { label: '备用电话', value: profile.backupPhone },
    { label: '所在公司', value: profile.companyName },
  ];

  const addressFields: { label: string; value: string | null }[] = [
    { label: '所在地区', value: profile.location },
    { label: '详细地址', value: profile.address },
  ];

  const emergencyFields: { label: string; value: string | null }[] = [
    { label: '联系人姓名', value: profile.emergencyContact },
    { label: '联系人电话', value: profile.emergencyPhone },
  ];

  const renderField = (label: string, value: string | null) => (
    <div key={label}>
      <p className={labelCls}>{label}</p>
      <p className={valueCls}>{value || '未填写'}</p>
    </div>
  );

  const SectionTitle = ({ title }: { title: string }) => (
    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider pt-2 pb-1 border-b border-gray-100 mb-4">{title}</p>
  );

  return (
    <div>
      {/* Header row with title and edit button */}
      <div className="flex items-center justify-between mb-5 md:mb-6">
        <div>
          <h2 className="text-base md:text-lg font-semibold text-gray-900">个人信息</h2>
          <p className="text-xs md:text-sm text-gray-500 mt-0.5">查看个人基本资料</p>
        </div>
        <button
          onClick={onEdit}
          className="flex items-center gap-1.5 px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-edit-line text-sm" />
          编辑资料
        </button>
      </div>

      {/* Basic Info */}
      <SectionTitle title="基本信息" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-6 mb-6">
        {infoFields.map(f => renderField(f.label, f.value))}
      </div>

      {/* Introduction */}
      {profile.introduction && (
        <>
          <SectionTitle title="个人简介" />
          <div className="mb-6">
            {renderField('简介', profile.introduction)}
          </div>
        </>
      )}

      {/* Address */}
      <SectionTitle title="地址信息" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-6 mb-6">
        {addressFields.map(f => renderField(f.label, f.value))}
      </div>

      {/* Emergency Contact */}
      <SectionTitle title="紧急联系人" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-6 mb-6">
        {emergencyFields.map(f => renderField(f.label, f.value))}
      </div>

      {/* Bottom edit button (mobile) */}
      <div className="sm:hidden mt-6">
        <button
          onClick={onEdit}
          className="w-full flex items-center justify-center gap-1.5 px-4 py-3 bg-teal-500 hover:bg-teal-600 text-white text-sm font-medium rounded-lg transition-colors cursor-pointer"
        >
          <i className="ri-edit-line text-sm" />
          编辑资料
        </button>
      </div>
    </div>
  );
}
